import React, { useCallback, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import _ from 'lodash';
import { useTableUniverse, useTableApi } from '~Components/TableUniverse';
import Articles from './Components/Articles';
import Button from './Components/TableButton';
import Payment from './Components/Payment';
import { useI18n, useStaffCan, useCurrency, useSelector, useDispatch, useButton } from '~Hooks';
import Messages from './Components/Messages';
import ServiceCharge from './Components/ServiceCharge';
import { filterOutServiceCharge } from '~Utils/serviceCharge';
import { useStorage } from '~Components/Storage';
import compressIcon from '~Assets/icons/compress.svg';
import expandIcon from '~Assets/icons/expand.svg';
import Icon from '~ComponentsImproved/Icon';
import { useSwipeable } from 'react-swipeable';
import * as actions from '~actions';
import { useCashDrawer } from '~Providers/CashDrawerProvider';
import { CashDrawerOpenInfo } from '~Scenes/POS/Components/Cart';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import { Box, RedButton } from '~Styles';
import RemoveItemModal from './Components/RemoveItemModal';
import Heading from '~ComponentsImproved/Typography/Heading';

const Wrapper = styled.div<{ isParkedOrder: boolean }>`
  background-color: ${({ isParkedOrder }) =>
    isParkedOrder ? 'var(--colorBlueGray1100)' : 'var(--colorBlueGray1300)'};
  flex: 1;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.24px;
  color: var(--colorWhite);
  padding: 0 0 16px 0;
  display: flex;
  flex-flow: column nowrap;
  height: calc(100% - 48px);
  box-sizing: border-box;
`;

const ItemsWrapper = styled.div`
  overflow: auto;
  position: relative;
  padding-bottom: 80px;
  box-sizing: border-box;
  display: flex;
  flex: 1;
  flex-flow: column nowrap;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const HeaderBox = styled(Box)<{ isParkedOrder?: boolean }>`
  padding: 8px 8px 8px 16px;
  border-bottom: 1px solid var(--colorBlueGray1100);
  color: ${({ isParkedOrder }) =>
    isParkedOrder ? 'var(--colorBlueGray200)' : 'var(--colorBlueGray800)'};
`;

const Qty = styled.div`
  width: 32px;
  margin-right: 8px;
`;

const BottomWrapper = styled.div<{ isParkedOrder: boolean }>`
  border-top: 1px solid var(--colorBlueGray1000);
  position: relative;
  box-sizing: border-box;
  display: flex;
  flex-flow: column nowrap;

  ${({ isParkedOrder }) =>
    `
    &:after {
      content: '';
      background: linear-gradient(to top, 
        ${isParkedOrder ? 'var(--colorBlueGray1100)' : 'rgba(14, 18, 22, 1)'} 0%, 
        ${isParkedOrder ? 'transparent' : 'rgba(14, 18, 22, 0)'} 100%
      );
      height: 60px;
      width: 100%;
      position: absolute;
      top: -60px;
      right: 0;
      pointer-events: none;
    }
  `}
`;

const BottomSection = styled.div`
  display: flex;
  flex-flow: column nowrap;
  flex: 1;
  position: relative;
  padding: 16px 16px 0;
  max-height: 50vh;
`;

const PriceInfo = styled.div`
  margin-bottom: 8px;
`;

const PayText = styled.p`
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.24px;
  color: var(--colorWhite);
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 0;
  margin-bottom: 4px;
`;

const DeleteButtonWrapper = styled.div`
  margin-top: auto;
  margin-bottom: 196px;
  padding: 16px 16px 0;
`;

const CompressButton = styled.div`
  padding: 4px;
  background: var(--colorBlueGray1200);
  border-radius: 2px;
`;

const RemoveButtonWrapper = styled.div`
  box-sizing: border-box;
  position: absolute;
  width: calc(100% - 32px);
  transform: translateY(-100%);
  z-index: 1;
  top: -4px;
  margin: 0 16px 16px;
`;

interface Props {
  tableId: string;
  tableName: string;
  selectedCheque: string | null;
  isBongVisible?: boolean;
  isParkedOrder?: boolean; // If the bill is a parked order we disable actions
  setSelectedCheque?: (id: string | null) => void;
  homelessItems?: boolean;
  showLogin?: () => void;
  onDelete?: () => void;
}

const Bill: React.FunctionComponent<Props> = ({
  tableId,
  tableName,
  selectedCheque,
  isBongVisible,
  isParkedOrder,
  setSelectedCheque,
  homelessItems,
  showLogin,
  onDelete,
}) => {
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [showRemoveModal, setShowRemoveModal] = useState<boolean>(false);
  const [showRemoveChequeButton, setShowRemoveChequeButton] = useState(false);
  const cashDrawer = useCashDrawer();

  const staffId = useSelector((state) =>
    state.companyStaff?.staffs.length ? state.staff : false,
  );
  const { i18n } = useI18n();
  const api = useTableApi();
  const universe = useTableUniverse();
  const currency = useCurrency();
  const [storage, setStorage] = useStorage();

  const cheque = universe.cheques.find((c) => c.id === selectedCheque);
  const chequeItems = universe.items.filter((c) =>
    homelessItems ? !c.chequeId : c.chequeId === selectedCheque,
  );
  const nonSelectedItems = chequeItems.filter((item) => !selectedItems.includes(item.id));
  const nonSelectedItemsTotalSum = nonSelectedItems.reduce((acc, item) => {
    return (acc += item.amount - item.discount);
  }, 0);

  // Unselect items when selecting another cheque
  useEffect(() => {
    if (selectedCheque) {
      setSelectedItems([]);
    }
  }, [selectedCheque]);

  // Wait with showing the remove cheque button until the user has had time to add items
  // to the cheque. If no items are added after 5 seconds, we show the button.
  useEffect(() => {
    setShowRemoveChequeButton(false);

    if (cheque && chequeItems.length === 0 && !isBongVisible) {
      const timeout = setTimeout(() => {
        setShowRemoveChequeButton(true);
      }, 5000);

      return () => clearTimeout(timeout);
    }

    return undefined;
  }, [cheque?.id, chequeItems.length, isBongVisible]);

  const staffCan = useStaffCan();
  // Currently we use REFUND permission
  const staffCanDelete = staffCan.REFUND;
  const staffCanMutate = cheque && staffCan.mutateCheque(cheque.id);

  const itemsWithoutServiceCharge = filterOutServiceCharge(chequeItems);

  const chequeMessages = universe.messages.filter((m) => m.chequeId === selectedCheque);

  /**
   * Deletes the selected cheque. Only available if the cheque is empty.
   */
  const handleDelete = useCallback(() => {
    api.removeCheque(selectedCheque!, cheque?.tableId || '');
    api.log.removeCheque(selectedCheque!, '--');

    if (onDelete) {
      onDelete();
    }

    if (setSelectedCheque) {
      setSelectedCheque(null);
    }
  }, [selectedCheque, setSelectedCheque]);

  const total = cheque ? cheque.amount - cheque.discount : 0;
  const leftToPay = cheque ? total - cheque.paidAmount : 0;

  const removeButton = useButton({
    onClick: () => setShowRemoveModal(true),
    // The remaining amount on the cheque (non selected items) must be more than the paid amount to allow removal
    disabled: cheque
      ? cheque.paidAmount > 0 && nonSelectedItemsTotalSum <= cheque.paidAmount
      : false,
    disabledToast: i18n('TM.CantRemoveItemsWithPayments'),
  });

  return (
    <Wrapper isParkedOrder={isParkedOrder || false}>
      {homelessItems && !cheque && showLogin && (
        <ItemsWrapper>
          <Articles
            items={itemsWithoutServiceCharge}
            staffId={staffId}
            showLogin={showLogin}
            selectedItems={selectedItems}
            setSelectedItems={setSelectedItems}
          />
        </ItemsWrapper>
      )}

      {cheque && (
        <>
          <HeaderBox
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            isParkedOrder={isParkedOrder || false}>
            <Box display="flex" alignItems="center">
              <Qty>
                <Heading level={5} color="var(--colorBlueGray600)">
                  {i18n('TM.Qty')}
                </Heading>
              </Qty>
              <Heading level={5} color="var(--colorBlueGray600)">
                {i18n('TM.Article')}
              </Heading>
            </Box>
            <CompressButton
              onTouchStart={() => {
                setStorage({
                  ...storage,
                  compressedCart: !storage.compressedCart,
                });
              }}>
              <Icon
                src={storage.compressedCart ? expandIcon : compressIcon}
                color="var(--colorBlueGray100)"
                width="20px"
                height="20px"
              />
            </CompressButton>
          </HeaderBox>
          <ItemsWrapper>
            <Articles
              items={itemsWithoutServiceCharge}
              staffId={staffId}
              showLogin={showLogin}
              selectedItems={selectedItems}
              setSelectedItems={(selection) => {
                if (isParkedOrder) {
                  // We don't allow modification of an order when it's parked
                  return;
                }

                setSelectedItems(selection);
              }}
              hasPartialPayment={cheque.paidAmount > 0}
            />
            {chequeMessages.length > 0 && <Messages messages={chequeMessages} />}
            {cheque.serviceCharge && (
              <ServiceCharge
                chequeId={cheque.id}
                percentage={cheque.serviceCharge.percentage}
                disableTouchAction={isParkedOrder}
              />
            )}
          </ItemsWrapper>

          <BottomWrapper isParkedOrder={isParkedOrder || false}>
            {selectedItems.length > 0 && (
              <>
                <RemoveButtonWrapper>
                  {staffCanDelete && (
                    <RedButton {...removeButton} fluid>
                      {i18n('TM.Remove')}
                    </RedButton>
                  )}
                </RemoveButtonWrapper>
                {showRemoveModal && (
                  <RemoveItemModal
                    selectedItems={selectedItems}
                    tableId={tableId}
                    tableName={tableName}
                    clearChosen={() => {
                      setSelectedItems([]);
                      setShowRemoveModal(false);
                      if (onDelete) {
                        onDelete();
                      }
                    }}
                    closeModal={() => {
                      setShowRemoveModal(false);
                    }}
                  />
                )}
              </>
            )}

            {chequeItems.length > 0 ? (
              <BottomSection>
                <div>
                  <PriceInfo>
                    {cheque?.serviceCharge && (
                      <PayText>
                        <span>
                          {i18n('ServiceCharge.Title')} {cheque.serviceCharge.percentage}%
                        </span>
                        {currency.format(cheque.serviceCharge.amount)}
                      </PayText>
                    )}
                    <PayText>
                      <span>{i18n('TM.Discount')}</span>
                      {currency.format(cheque.discount)}
                    </PayText>
                    {cheque.paidAmount > 0 && (
                      <PayText>
                        <span>{i18n('TM.Payed')}</span>
                        {currency.format(cheque.paidAmount)}
                      </PayText>
                    )}
                    <PayText>
                      <span>{i18n('TM.Total')}</span>
                      {currency.format(leftToPay)}
                    </PayText>
                  </PriceInfo>
                  {cashDrawer.isOpen ? (
                    <CashDrawerOpenInfo>
                      <Paragraph level={3} color="var(--colorBlueGray1200)">
                        {i18n('Cashdrawer.BlockedPayments')}
                      </Paragraph>
                    </CashDrawerOpenInfo>
                  ) : (
                    staffCanMutate &&
                    showLogin && (
                      <Payment
                        tableName={tableName}
                        tableId={tableId}
                        selectedCheque={selectedCheque}
                        selectedItems={selectedItems}
                        clearSelectedItems={() => setSelectedItems([])}
                        hasPartialPayment={cheque.paidAmount > 0}
                      />
                    )
                  )}
                </div>
              </BottomSection>
            ) : (
              <DeleteButtonWrapper>
                {staffCanDelete && staffCanMutate && showRemoveChequeButton && (
                  <Button
                    bg="linear-gradient(-180deg, rgb(153 32 32) 0%, rgb(132 23 23) 100%)"
                    color="#fff"
                    large={true}
                    onTouchStart={handleDelete}>
                    <span>{i18n('TM.RemoveCheque')}</span>
                  </Button>
                )}
              </DeleteButtonWrapper>
            )}
          </BottomWrapper>
        </>
      )}
    </Wrapper>
  );
};

export default React.memo(Bill);
